import { useState } from "react";
import { Check } from "lucide-react";
import { IMAGES } from "../data";
import { Reveal } from "./Reveal";
import { EnquiryForm } from "./EnquiryForm";

const ROOMS = [
  {
    name: "Deluxe Room",
    desc: "Comfortable and elegant room for a relaxing stay.",
    price: "₹2,499",
    img: IMAGES.deluxe,
    alt: "Deluxe room at Kasdol Hotel with comfortable bed and warm lighting",
    features: ["Comfortable Bed", "Attached Bathroom", "Air Conditioning", "Wi-Fi", "Room Service"],
    testid: "room-card-deluxe",
  },
  {
    name: "Premium Room",
    desc: "A more spacious room designed for a comfortable stay.",
    price: "₹3,499",
    img: IMAGES.premium,
    alt: "Premium room at Kasdol Hotel with king bed and elegant interiors",
    features: ["King/Queen Bed", "Attached Bathroom", "Air Conditioning", "Wi-Fi", "Room Service"],
    testid: "room-card-premium",
  },
];

const FIELDS = [
  { name: "check_in", label: "Check-in", type: "date", required: true, testid: "rooms-form-check-in" },
  { name: "check_out", label: "Check-out", type: "date", required: true, testid: "rooms-form-check-out" },
  { name: "guests", label: "Guests", type: "number", required: true, placeholder: "e.g. 2", testid: "rooms-form-guests" },
  {
    name: "room_type",
    label: "Room Type",
    type: "select",
    required: true,
    options: ["Deluxe Room", "Premium Room"],
    testid: "rooms-form-room-type",
  },
  { name: "name", label: "Name", type: "text", required: true, placeholder: "Your full name", testid: "rooms-form-name" },
  { name: "phone", label: "Phone Number", type: "tel", required: true, placeholder: "10-digit mobile number", testid: "rooms-form-phone" },
];

export const Rooms = () => {
  const [roomType, setRoomType] = useState("");

  const validateDates = (values) => {
    const errs = {};
    if (values.check_in && values.check_out && values.check_out <= values.check_in) {
      errs.check_out = "Check-out must be after check-in";
    }
    return errs;
  };

  return (
    <section id="rooms" data-testid="rooms-section" className="bg-ivory py-16 sm:py-20 lg:py-28">
      <div className="section-wrap">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">Stay With Us</p>
          <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight text-navy sm:text-4xl lg:text-5xl">
            Comfortable Rooms for a Relaxing Stay
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          {ROOMS.map((room, i) => (
            <Reveal key={room.name} delay={i * 150}>
              <article
                data-testid={room.testid}
                className="group h-full overflow-hidden rounded-md bg-white shadow-md transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={room.img}
                    alt={room.alt}
                    loading="lazy"
                    className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-72"
                  />
                  <span className="absolute bottom-4 left-4 rounded-sm bg-navy/90 px-4 py-1.5 text-sm font-semibold text-cream backdrop-blur-sm">
                    {room.price} <span className="text-xs font-normal text-cream/70">/ night</span>
                  </span>
                </div>
                <div className="p-6 sm:p-8">
                  <h3 className="font-serif text-2xl font-semibold text-navy">{room.name}</h3>
                  <p className="mt-2 text-sm text-ink/70">{room.desc}</p>
                  <ul className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                    {room.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-ink/75">
                        <Check className="h-4 w-4 shrink-0 text-gold" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-xs italic text-ink/45">Pricing shown is demo content.</p>
                  <a
                    href="#rooms-availability"
                    data-testid={`${room.testid}-book`}
                    onClick={() => setRoomType(room.name)}
                    className="btn-gold mt-5 w-full sm:w-auto"
                  >
                    Book This Room
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={100}>
          <div id="rooms-availability" className="mx-auto mt-14 max-w-3xl scroll-mt-28 rounded-md border border-gold/25 bg-white p-6 shadow-xl sm:p-10">
            <h3 className="font-serif text-2xl font-semibold text-navy sm:text-3xl">Check Availability</h3>
            <p className="mt-2 text-sm text-ink/60">
              Share your dates and we will confirm availability on call.
            </p>
            <div className="mt-7">
              <EnquiryForm
                key={roomType}
                formTestId="rooms-availability-form"
                submitTestId="rooms-form-submit"
                fields={FIELDS}
                submitLabel="Check Availability"
                service="room"
                initialValues={roomType ? { room_type: roomType } : {}}
                customValidate={validateDates}
                successTitle="Availability Enquiry Received"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
